import { createContext, useCallback, useContext, type ReactNode } from "react";
import type { Permission, Session } from "@/api/types";
import { useSession } from "@/api/queries";
import { HttpError } from "@/api/http";
import { LoginPage } from "@/features/system/LoginPage";
import { SET_PASSWORD_PATH, SetPasswordPage } from "@/features/system/SetPasswordPage";
import { SIGNUP_PATH, SignupPage } from "@/features/system/SignupPage";
import { nameTaxes } from "@/lib/taxWords";

interface SessionContextValue {
  session: Session;
  can: (p: Permission) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * The invitation's link needs no session, and whoever opens it may not have one, so it comes before
 * asking. Otherwise this loads the session and exposes `can()` for permission-aware UI.
 */
export function SessionProvider(props: { children: ReactNode; fallback: ReactNode }) {
  if (window.location.pathname === SET_PASSWORD_PATH) return <SetPasswordPage />;
  if (window.location.pathname === SIGNUP_PATH) return <SignupPage />;
  return <SessionGate {...props} />;
}

function SessionGate({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const { data, isError, error, refetch } = useSession();
  // No session yet: the API answers 401 and the SPA shows its own login page (ADR-0003). A
  // refetch after the cookie is issued swaps the page for the shell without a reload.
  if (isError && error instanceof HttpError && error.problem.status === 401) {
    return <LoginPage onSignedIn={() => void refetch()} />;
  }
  if (isError) {
    return (
      <div className="p-8 text-sm text-bad-text">
        Could not load your session. {(error as Error).message}
      </div>
    );
  }
  if (!data) return <>{fallback}</>;
  const granted = new Set<Permission>(data.permissions);
  return (
    <SessionContext.Provider value={{ session: data, can: (p) => granted.has(p) }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useCurrentSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useCurrentSession must be used inside <SessionProvider>");
  return ctx;
}

/**
 * Says a sentence in this organisation's names for its taxes (ADR-0036). Outside a session, as a
 * lone component in a test is, the sentence stands as written.
 */
export function useTaxWords(): (text: string) => string {
  const names = useContext(SessionContext)?.session.taxNames;
  return useCallback((text: string) => (names ? nameTaxes(text, names) : text), [names]);
}

/** Whether this deployment can make PDFs of documents. */
export function useDocumentPdfEnabled(): boolean {
  return useContext(SessionContext)?.session.documentPdf === true;
}

/** Whether this deployment can invite new people by email. */
export function useInvitationsEnabled(): boolean {
  return useContext(SessionContext)?.session.invitations === true;
}

/** Whether this deployment can email documents. */
export function useDocumentMailEnabled(): boolean {
  return useContext(SessionContext)?.session.documentMail === true;
}

/** The applied pack's name, e.g. Nepal. Null when none is applied, or outside a session. */
export function usePackName(): string | null {
  return useContext(SessionContext)?.session.taxNames.packName ?? null;
}

export function useCan(): (p: Permission) => boolean {
  return useCurrentSession().can;
}

const NOTHING: (p: Permission) => boolean = () => false;

/**
 * `useCan`, for a screen that can render before or without a session (the locked books page): with no
 * session, nothing is permitted.
 */
export function useCanIfSignedIn(): (p: Permission) => boolean {
  return useContext(SessionContext)?.can ?? NOTHING;
}

/** The session, or null where a screen can render without one. */
export function useSessionIfSignedIn(): SessionContextValue | null {
  return useContext(SessionContext);
}
